import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Modal, Table, Tooltip, message } from "antd";
import { AlertTriangle, KeyRound, Loader, MonitorPlay, Plus, RefreshCw, Trash2, Timer } from "lucide-react";
import { AN_COLORS, AN_MONO } from "../../../design-system/tokens";
import { CountChip, RowActionMenu, TableCard, anTableProps, fmtDate, tableEmpty } from "../../../design-system/tableUi";
import { Note, PanelHead } from "../../../pages/enduser/ssh-proxy/sshProxyUi";
import {
  listLocalUsers,
  passwordErrorText,
  removeManagedAccount,
} from "../../../service/sshProxyService";
import AccountRotationModal from "../../../pages/enduser/password-rotation/AccountRotationModal";
import RevealPasswordModal from "../../../pages/enduser/password-rotation/RevealPasswordModal";
import { AddManagedAccountModal, addUserLimitsOf, relativeTime } from "./EndpointManagedAccountsTab";

// The endpoint details page's "Local users" for administrators: one table of
// the endpoint's accounts — what the scan found, root when reported, and the
// users Authnull added — with its password state and password rotation
// (listLocalUsers). Administrators only; others see EndpointDetailsUser.
//
// The Add user dialog lives here so the table header and the page's ⋮ menu
// share it: the page passes `addOpen` / `onAddOpenChange`. Bump `refreshKey`
// to reload (e.g. after a scan finished).

// Re-read while a user is being added / removed or a rotation is running.
const POLL_MS = 4000;
const POLL_MAX = 15;

const muted = { fontSize: 11.5, color: AN_COLORS.muted };
const IN_FLIGHT = new Set(["pending", "removing"]);

// The scan's password status (passwd -S): set, locked, or no password.
const PASSWORD_STATE_LABEL = { P: "Set", L: "Locked", NP: "None" };

const busyRow =(u) => IN_FLIGHT.has(u.managed?.status) || !!u.rotation?.jobRunning;

const TAG_STYLE = {
  display: "inline-block", fontFamily: AN_MONO, fontSize: 10.5, letterSpacing: 0.4, textTransform: "uppercase",
  padding: "1px 6px", borderRadius: 4, whiteSpace: "nowrap",
};

function AddedTag() {
  return <span style={{ ...TAG_STYLE, background: AN_COLORS.primaryL, color: AN_COLORS.primaryD }}>Added</span>;
}

// The sudo account the endpoint was onboarded with: Authnull manages the
// machine through it, so it is never rotated, rotated on demand or revealed.
function AdminTag() {
  return (
    <Tooltip title="The account Authnull uses to set up and manage this machine. Its password is never rotated or revealed.">
      <span style={{ ...TAG_STYLE, background: AN_COLORS.warnBg, color: AN_COLORS.warnFg }}>Admin account</span>
    </Tooltip>
  );
}

function RotationCell({ rotation }) {
  const r = rotation || {};
  const failed = r.status === "failed";
  const label = !r.enabled
    ? "Off"
    : !r.own && r.profile
    ? `Profile: ${r.profile}`
    : r.intervalDays
    ? `Every ${r.intervalDays} day${r.intervalDays === 1 ? "" : "s"}`
    : "On";
  return (
    <div style={{ display: "grid", gap: 3, justifyItems: "start" }}>
      <span style={{ fontSize: 12.5, color: r.enabled ? AN_COLORS.ink2 : AN_COLORS.muted }}>{label}</span>
      {failed && (
        <Tooltip title={r.detail || "The last rotation failed."}>
          <span
            style={{
              display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11.5, color: AN_COLORS.danger,
              maxWidth: 280, whiteSpace: "normal",
            }}
          >
            <AlertTriangle size={12} style={{ flexShrink: 0 }} />
            Rotation failed{r.detail ? `: ${r.detail}` : ""}
          </span>
        </Tooltip>
      )}
      {r.jobRunning && (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, ...muted }}>
          <Loader size={12} /> Rotating…
        </span>
      )}
    </div>
  );
}

export default function LocalUsersTable({ instanceId, hostName, readOnly = false, refreshKey, addOpen, onAddOpenChange }) {
  const [messageApi, msgCtx] = message.useMessage();
  const [modalApi, modalCtx] = Modal.useModal();
  const [data, setData] = useState(null); // the listLocalUsers response; null while loading
  const [error, setError] = useState("");
  const [rotating, setRotating] = useState(null); // the row whose rotation dialog is open
  const [revealing, setRevealing] = useState(null); // the row being revealed
  const polls = useRef(0);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(
    ({ quiet = false } = {}) => {
      if (!instanceId) return;
      if (!quiet) setError("");
      listLocalUsers({
        instanceId: Number(instanceId),
        onSuccess: (res) => {
          if (!mounted.current) return;
          setData(res);
          setError("");
        },
        onFailure: (err) => {
          if (!mounted.current) return;
          setData((d) => d || { users: [] });
          setError(passwordErrorText(err, "Couldn't load the endpoint's local users."));
        },
      });
    },
    [instanceId],
  );

  useEffect(() => {
    setData(null);
    polls.current = 0;
    load();
  }, [load, refreshKey]);

  // A few re-reads while anything is on its way somewhere.
  const users = data?.users || [];
  const busy = users.some(busyRow);
  useEffect(() => {
    if (!busy || polls.current >= POLL_MAX) return undefined;
    const t = setTimeout(() => {
      polls.current += 1;
      load({ quiet: true });
    }, POLL_MS);
    return () => clearTimeout(t);
  }, [busy, data, load]);

  const refresh = () => {
    polls.current = 0;
    load({ quiet: true });
  };

  const limits = addUserLimitsOf(data);
  const host = hostName || "this endpoint";

  // ─── Row actions ─────────────────────────────────────────────────────────

  const confirmRemove = (u) =>
    modalApi.confirm({
      title: `Remove ${u.account}?`,
      content: `Delete ${u.account} from ${host} now? Its sessions end and any password it has is retired.`,
      okText: "Remove user",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: () =>
        new Promise((resolve) => {
          removeManagedAccount({
            id: u.managed.id,
            onSuccess: () => {
              resolve();
              if (!mounted.current) return;
              setData((d) => ({
                ...d,
                users: (d?.users || []).map((x) =>
                  x.account === u.account ? { ...x, managed: { ...x.managed, status: "removing" } } : x,
                ),
              }));
              messageApi.success(`Removing ${u.account} from ${host}.`);
              polls.current = 0;
            },
            onFailure: (err) => {
              resolve();
              if (!mounted.current) return;
              if (err?.response?.status === 409) messageApi.info("It's already being removed.");
              else messageApi.error(passwordErrorText(err, "Couldn't remove the user."));
              refresh();
            },
          });
        }),
    });

  const onCreated = (_managed, account) => {
    if (!mounted.current) return;
    messageApi.success(`${account} is being added — it shows as active within about a minute.`);
    refresh();
  };

  // ─── Table ───────────────────────────────────────────────────────────────
  const columns = [
    {
      title: "User",
      key: "account",
      render: (_, u) => {
        const st = u.managed?.status;
        return (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <span style={{ fontFamily: AN_MONO, fontSize: 12.5, fontWeight: 500, color: AN_COLORS.ink }}>{u.account}</span>
            {u.added && <AddedTag />}
            {u.admin && <AdminTag />}
            {IN_FLIGHT.has(st) && <span style={muted}>{st}</span>}
          </span>
        );
      },
    },
    {
      title: "Password",
      key: "password",
      width: 110,
      render: (_, u) => {
        const label = PASSWORD_STATE_LABEL[u.passwordState] || "—";
        return (
          <span style={{ fontSize: 12.5, color: u.passwordState === "P" ? AN_COLORS.ink2 : AN_COLORS.muted }}>{label}</span>
        );
      },
    },
    {
      title: "Rotation",
      key: "rotation",
      render: (_, u) => (u.admin ? <span style={{ fontSize: 12.5, color: AN_COLORS.muted }}>Not rotated</span> : <RotationCell rotation={u.rotation} />),
    },
    {
      title: "Last rotated",
      key: "rotatedAt",
      width: 180,
      render: (_, u) => (
        <span style={{ fontSize: 12.5, color: u.rotation?.rotatedAt ? AN_COLORS.ink2 : AN_COLORS.muted }}>
          {fmtDate(u.rotation?.rotatedAt)}
        </span>
      ),
    },
    {
      title: "Expires",
      key: "expires",
      width: 180,
      render: (_, u) => {
        if (!u.added) return <span style={{ color: AN_COLORS.muted }}>—</span>;
        const at = u.managed?.expiresAt;
        if (!at) return <span style={{ fontSize: 12.5, color: AN_COLORS.ink2 }}>Never</span>;
        const rel = relativeTime(at);
        return (
          <div style={{ display: "grid", gap: 2 }}>
            <span style={{ fontSize: 12.5, color: AN_COLORS.ink2 }}>{fmtDate(at)}</span>
            {rel && <span style={{ ...muted, color: rel === "expired" ? AN_COLORS.warn : AN_COLORS.muted }}>{rel}</span>}
          </div>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: 48,
      align: "right",
      render: (_, u) => {
        const r = u.rotation || {};
        const st = u.managed?.status;
        // An added user can be changed once it is active on the machine.
        const settled = !u.added || st === "active";
        const items = [];
        // Rotation can be set where the account has, or may have, a password:
        // set ("P"), unknown (""), or an added user that logs in with one.
        // Never on the admin account: the backend refuses it.
        const mayHavePassword = u.passwordState === "P" || u.passwordState === "" || (u.added && u.managed?.credential === "password");
        if (!readOnly && !u.admin && (mayHavePassword || r.enabled)) {
          items.push({
            key: "rotation",
            icon: <Timer size={13} />,
            label: "Password rotation…",
            disabled: !settled,
            onClick: () => setRotating(u),
          });
        }
        if (u.revealable && !u.admin) {
          items.push({
            key: "reveal",
            danger: true,
            icon: <KeyRound size={13} />,
            label: (
              <span>
                Reveal… <span style={{ fontSize: 11, opacity: 0.75 }}>break-glass</span>
              </span>
            ),
            onClick: () => setRevealing(u),
          });
        }
        if (!readOnly && u.added && u.managed?.id) {
          items.push({
            key: "remove",
            danger: true,
            icon: <Trash2 size={13} />,
            label: "Remove user…",
            disabled: st === "removing",
            onClick: () => confirmRemove(u),
          });
        }
        if (items.length) items.push({ type: "divider" });
        items.push({
          key: "session-recordings",
          icon: <MonitorPlay size={13} />,
          label: (
            <Link to={`/pam/jumpServer-sessionRecordings?epmUser=${encodeURIComponent(u.account || "")}`}>
              View session recordings
            </Link>
          ),
        });
        return <RowActionMenu ariaLabel={`Actions for ${u.account}`} items={items} />;
      },
    },
  ];

  const loading = data === null;

  return (
    <section>
      {msgCtx}
      {modalCtx}
      <PanelHead
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
            Local users {!loading && <CountChip count={users.length} label={users.length === 1 ? "user" : "users"} />}
          </span>
        }
        desc="Local accounts on this machine, and the users Authnull added. Accounts stay current automatically — the endpoint is re-scanned daily."
        right={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <Button onClick={refresh} icon={<RefreshCw size={13} style={{ verticalAlign: -2 }} />}>
              Refresh
            </Button>
            {!readOnly && (
              <Button type="primary" icon={<Plus size={14} style={{ verticalAlign: -2 }} />} onClick={() => onAddOpenChange(true)}>
                Add user
              </Button>
            )}
          </span>
        }
      />
      {error && (
        <div style={{ marginBottom: 12 }}>
          <Note tone="danger">{error}</Note>
        </div>
      )}
      <TableCard>
        <Table
          {...anTableProps}
          rowKey={(u) => u.account}
          columns={columns}
          dataSource={users}
          loading={loading}
          scroll={{ x: "max-content" }}
          pagination={users.length > 20 ? { pageSize: 20, showSizeChanger: false } : false}
          locale={tableEmpty(loading, "No local users found yet. Scan accounts from the ⋮ menu.")}
        />
      </TableCard>

      <AddManagedAccountModal
        open={!!addOpen}
        instanceId={instanceId}
        hostname={hostName}
        limits={data ? limits : undefined}
        existingAccounts={users.map((u) => u.account)}
        onClose={() => onAddOpenChange(false)}
        onCreated={onCreated}
      />
      <AccountRotationModal
        open={!!rotating}
        instanceId={instanceId}
        account={rotating?.account}
        hostname={hostName}
        rotation={rotating?.rotation}
        defaultIntervalDays={limits.defaultIntervalDays}
        onClose={() => setRotating(null)}
        onRotated={() => {
          messageApi.success(`Rotating ${rotating?.account}'s password now.`);
          refresh();
        }}
        onSaved={(rotation) => {
          const account = rotating?.account;
          setRotating(null);
          messageApi.success(
            rotation?.enabled ? `${account}'s password rotates every ${rotation.intervalDays} days.` : `Rotation of ${account} turned off.`,
          );
          refresh();
        }}
      />
      <RevealPasswordModal
        open={!!revealing}
        endpointId={Number(instanceId)}
        account={revealing?.account}
        hostname={hostName}
        onClose={() => {
          setRevealing(null);
          refresh();
        }}
      />
    </section>
  );
}
